'use client'

import { clsx } from 'clsx'
import { type ComponentProps, type FC, useEffect, useRef } from 'react'

import { useAdServerCloseBannerControls } from '../../useAdServerCloseBannerControls'
import { useAdServerConfigurationContext } from '../AdServerConfigurationContextProvider'

import { AdServerBannerCloseButton } from './AdServerBannerCloseButton'
import { AdServerBannerLegalText } from './AdServerBannerLegalText'
import { AdServerBannerWrapper } from './AdServerBannerWrapper'

type AdSlotDimensions = {
  height: number
  width: number
}

export type AdBannerProps = ComponentProps<'div'> & {
  bannerClasses?: string
  bannerTags: string[]
  dimensions: AdSlotDimensions
  optional?: boolean

  /**
   * Reserves the space for the banner by displaying a skeleton with banner sizes
   */
  showSkeleton?: boolean
}

export const AdServerBannerContent: FC<
  AdBannerProps & { isCarousel: boolean }
> = ({
  bannerClasses: bannerClassesProp,
  bannerTags,
  className,
  dimensions,
  showSkeleton = false,
  ...restProps
}) => {
  const { getBanner, initBanners } = useAdServerConfigurationContext()
  const bannerRef = useRef<HTMLDivElement>(null)
  const bannerContentRef = useRef<HTMLDivElement>(null)

  const bannerClasses = clsx('as-placeholder', bannerClassesProp)

  const bannerData = getBanner({
    height: dimensions.height,
    id: bannerTags.toString(),
    width: dimensions.width,
  })

  useEffect(() => {
    // if banner data is not present on the context then we need to fetch it
    if (!bannerData) {
      initBanners([
        {
          height: dimensions.height,
          id: bannerTags.toString(),
          width: dimensions.width,
        },
      ])
    }
  }, [bannerData, bannerTags, dimensions.height, dimensions.width, initBanners])

  useEffect(() => {
    if (!bannerData) {
      return
    }

    if (bannerData.bannerElement && (showSkeleton || bannerData.metadata)) {
      // we replace the adBanner placeholder with the actual banner and add banner specific classes
      bannerData.bannerElement.className = bannerClasses

      bannerRef.current = bannerData.bannerElement
      bannerContentRef.current?.replaceWith(bannerRef.current)
    }
  }, [bannerClasses, bannerData, showSkeleton])

  const closeBannerControls = useAdServerCloseBannerControls({
    metaDataContent: bannerData?.metadata,
  })

  if (!closeBannerControls.showBanner) {
    return null
  }

  const legalButton = bannerData?.metadata?.customData?.legalButton

  return (
    <AdServerBannerWrapper
      bannerData={bannerData}
      bannerRef={bannerRef}
      className={clsx('ad-server-banner', className)}
      {...restProps}
    >
      <div
        className={bannerClasses}
        ref={bannerContentRef}
        style={
          showSkeleton
            ? {
                aspectRatio: dimensions.width / dimensions.height,
                width: `${dimensions.width}px`,
              }
            : undefined
        }
      />
      {closeBannerControls.showCloseButton ? (
        <AdServerBannerCloseButton onClick={closeBannerControls.handleClose} />
      ) : null}
      {legalButton?.label && legalButton.text ? (
        <AdServerBannerLegalText
          label={legalButton.label}
          text={legalButton.text}
        />
      ) : null}
    </AdServerBannerWrapper>
  )
}
